import React from "react";
import { Modal } from "react-bootstrap";
import { connect } from "react-redux";
import type { Cell } from "react-table";
import type { Dispatch } from "redux";

import { classNames } from "@/lib/util";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import DraggableModal from "@/modules/pg/shared/DraggableModal";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import ReactTable, { ReactTableColumn } from "@/modules/shared/ui/ReactTable";
import { LinesOrdersMaps } from "@/modules/pg/shared/planningTable/reducers/linesOrders/mappers";
import { UPDATE_CHUNKED_ORDERS } from "@/modules/pg/shared/planningTable/reducers/linesOrders";

import { ORDER_TAGS } from "@/lib/ManufacturingTags.generated";
import { getBackend } from "@/lib/backend/backend2";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { UserActions } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";

import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type { OnOrderClickArgsActionTypes } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";

import "./ChunkingModal.scss";

type ChunkOrderState = t.IOrderProducedModel & {
    idx: number;
    has_error: boolean;
    remove?: void;
    quantity_open: number;
    quantity_produced: number;
    quantity_total: number;
    norm_base_units: number;
    line_title?: string;
    disable_remove?: boolean;
};

type ParentOrderDisplayRow = Pick<
    ChunkOrderState,
    "external_id" | "process_num" | "quantity_produced" | "quantity_total" | "quantity_open"
>;

type InputProps = {
    idx: number;
    cell: Cell<ChunkOrderState>;
    value: string;
    onChangeCell: (idx: number) => (e: React.ChangeEvent<HTMLElement>) => void;
};

const Input = (props: InputProps) => {
    const { cell, value, onChangeCell } = props;
    const idx = cell.row.values.idx;
    return (
        <input
            key={"quantity-total-" + idx}
            id={"quantity-total-" + idx}
            className={classNames("form-control")}
            type="number"
            min={cell.row.values.quantity_produced}
            value={value}
            onChange={e => onChangeCell(idx)(e)}
        />
    );
};

const getParentOrderColumns = (): ReactTableColumn<ParentOrderDisplayRow>[] => {
    return [
        {
            Header: translate("OrderTableProduction.external_id", "Order ID"),
            accessor: "external_id",
            disableFilters: true,
        },
        {
            Header: translate("common.process_num2", "Op."),
            accessor: "process_num",
            disableFilters: true,
        },
        {
            Header: translate("common.produced", "Produced"),
            accessor: "quantity_produced",
            disableFilters: true,
        },
        {
            Header: translate("common.open", "Open"),
            accessor: "quantity_open",
            disableFilters: true,
        },
        {
            Header: translate("common.total", "Total"),
            accessor: "quantity_total",
            disableFilters: true,
        },
    ];
};

const getChunkOrdersColumns = (): ReactTableColumn<ChunkOrderState>[] => {
    return [
        {
            Header: translate("common.id", "ID"),
            accessor: "idx",
            disableFilters: true,
        },
        {
            Header: translate("common.produced", "Produced"),
            accessor: "quantity_produced",
            disableFilters: true,
        },
        {
            Header: translate("common.open", "Open"),
            accessor: "quantity_open",
            disableFilters: true,
            Cell: Input,
        },
        {
            Header: translate("common.line", "Line"),
            accessor: "line_title",
            disableFilters: true,
        },
        {
            Header: "",
            accessor: "disable_remove",
            disableFilters: true,
            Cell: ({ cell }) => {
                const disable_remove = cell.row.values.disable_remove;

                return (
                    <button disabled={disable_remove} className="btn btn-primary">
                        {translate("common.remove", "Remove")}
                    </button>
                );
            },
        },
        {
            Header: "uuid",
            accessor: "uuid",
            disableFilters: true,
        },
    ];
};

type OwnProps = {
    show: boolean;
    order_uuid: string;
    onClose: () => void;
};

type Props = OwnProps & ReduxProps;

type State = {
    split_orders_chunks: ChunkOrderState[];
    removable_chunks: number[];
};

class ChunkingModal extends React.Component<Props, State> {
    state: State = {
        split_orders_chunks: [],
        removable_chunks: [],
    };

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prev_props: Props) {
        if (prev_props.show !== this.props.show && this.props.show) {
            this.load();
        }
    }

    async load() {
        let parent_order_uuid = this.getOrderUuid();
        const splits = this.props.split_orders.filter(c => c.parent.uuid === parent_order_uuid)[0];
        if (splits) {
            this.setState({
                split_orders_chunks: splits.chunks.map((c, idx) => ({
                    ...c,
                    idx,
                    has_error: false,
                    quantity_open: c.quantity_total - c.quantity_produced,
                })),
            });
        } else {
            const parent_order = this.getParentOrder();
            if (!parent_order) {
                return;
            }

            const quantity_to_split = parent_order.quantity_total;
            let num_of_chunks = 3; // HARDCODED - by default 3 chunks, we ageed on it
            const total_number_of_retorts = Math.floor(quantity_to_split / parent_order.norm_base_units);

            // if we have less retorts than chunks, we need to adjust the number of chunks
            if (total_number_of_retorts < num_of_chunks) {
                num_of_chunks = total_number_of_retorts + 1;
            }

            // basic/equal distribution of retorts between chunks
            const num_of_retorts_in_chunk_equal = Math.floor(
                quantity_to_split / parent_order.norm_base_units / num_of_chunks
            );

            // total amount units fit in retorts
            const total_units_in_retorts = total_number_of_retorts * parent_order.norm_base_units;

            // This section handles leftover retorts after initial distribution.
            // For instance, if we have 5 retorts and 3 chunks, we will have 2 retorts remaining after
            // evenly distributing the retorts among the chunks.
            // This means we need to further distribute the remaining retorts -
            // adding 1 retort to the first chunk and another to the second chunk.
            const chunk_leftover = total_number_of_retorts % num_of_chunks;

            // This section handles the leftover material units after distributing them among the retorts.
            // For instance, if we have 2400 material units, 3 retorts, 3 chunks, and a norm_base_units value of 790,
            // we initially distribute the material units among the retorts, giving us 3 * 790 = 2370 material units in retorts.
            // This leaves us with 30 material units (2400 - 2370) that have not been assigned to a retort.
            // These leftover units are then added to the last chunk.
            const retorts_leftover = quantity_to_split - total_units_in_retorts;

            const split_orders_chunks: ChunkOrderState[] = [];
            for (let index = 0; index < num_of_chunks; index++) {
                const { uuid } = await getBackend().common.getUuid({});
                let amount_of_units_in_chunk = num_of_retorts_in_chunk_equal * parent_order.norm_base_units;

                // add leftover retort to the first chunk
                if (index == 0 && (chunk_leftover == 1 || chunk_leftover == 2)) {
                    amount_of_units_in_chunk += parent_order.norm_base_units;
                }

                // add leftover retort to the second chunk
                if (index == 1 && chunk_leftover == 2) {
                    amount_of_units_in_chunk += parent_order.norm_base_units;
                }

                // add leftover material units to the last chunk
                if (index == num_of_chunks - 1) {
                    amount_of_units_in_chunk += retorts_leftover;
                }

                split_orders_chunks.push({
                    idx: index,
                    uuid,
                    external_id: parent_order.external_id,
                    process_num: parent_order.process_num,
                    quantity_produced: 0,
                    quantity_total: amount_of_units_in_chunk,
                    quantity_open: amount_of_units_in_chunk, // because quantity_produed = 0 quantity_open = quantity_total
                    line_uuid: parent_order.line_uuid,
                    parent_order_uuid: parent_order.uuid,
                    has_error: false,
                    norm_base_units: parent_order.norm_base_units,
                } as ChunkOrderState);
            }
            this.setState({
                split_orders_chunks,
            });
        }
    }

    getLineTitle = (line_uuid: string) => {
        const line_orders = this.props.lines_orders;

        if (line_orders === undefined || line_orders.length === 0) {
            return "No title";
        }

        const line = line_orders.find(l => l.line_uuid === line_uuid);

        if (line === undefined) {
            return "No title";
        }

        return line.line_title;
    };

    getParentOrderRow = (): ParentOrderDisplayRow[] => {
        const parent_order = this.getParentOrder();
        if (!parent_order) {
            return [];
        }
        const chunk_orders = this.state.split_orders_chunks;
        const sum = chunk_orders.reduce((acc, c) => acc + c.quantity_produced, 0);
        const produced = parent_order.quantity_produced + sum;
        const quantity_open = parent_order.quantity_total - produced;
        return [
            {
                external_id: parent_order.external_id,
                process_num: parent_order.process_num,
                quantity_produced: produced,
                quantity_total: parent_order.quantity_total,
                quantity_open,
            },
        ];
    };

    getParentOrder = () => {
        const parent = LinesOrdersMaps.parents_of_chunks.get(this.getOrderUuid());
        const order = LinesOrdersMaps.all_orders_map.get(this.getOrderUuid());
        // a parent order is either an existing parent order or a fresh order without chunks
        return parent || order;
    };

    onClickRemove = (uuid: string) => {
        const parent_order = this.getParentOrder();
        if (!parent_order) return;
        const chunk_orders = this.state.split_orders_chunks;
        const index = chunk_orders.findIndex(c => c.uuid === uuid);
        chunk_orders.splice(index, 1);
        this.setState({ split_orders_chunks: [...chunk_orders] });
    };

    onClickPlus = async () => {
        const parent_order = this.getParentOrder();
        if (!parent_order) return;
        const chunk_orders = this.state.split_orders_chunks;
        const sum = chunk_orders.reduce((acc, c) => acc + c.quantity_open, 0);
        const idx = Math.max(0, chunk_orders.length);
        const parent_quantity_open = parent_order.quantity_total - parent_order.quantity_produced;
        const quantity_total = idx > 0 ? Math.max(parent_quantity_open - sum, 0) : parent_quantity_open;
        const { uuid } = await getBackend().common.getUuid({});
        chunk_orders.push({
            idx,
            uuid,
            external_id: parent_order.external_id,
            process_num: parent_order.process_num,
            quantity_produced: 0,
            quantity_total,
            quantity_open: quantity_total, // because quantity_produced is 0
            line_uuid: parent_order.line_uuid,
            parent_order_uuid: parent_order.uuid,
            has_error: false,
            norm_base_units: parent_order.norm_base_units,
        } as ChunkOrderState);

        this.setState({
            split_orders_chunks: chunk_orders,
            removable_chunks: [...this.state.removable_chunks, idx],
        });
    };

    checkSum = () => {
        const parent_order = this.getParentOrder();
        if (!parent_order) return { is_sum_ok: false, missing: 0 };
        const chunk_orders = this.state.split_orders_chunks;
        const sum = chunk_orders.reduce((acc, c) => acc + c.quantity_open, 0);
        const quantity_produced =
            parent_order.quantity_produced + chunk_orders.reduce((acc, c) => acc + c.quantity_produced, 0);
        return {
            is_sum_ok: sum === parent_order.quantity_total - quantity_produced,
            missing: parent_order.quantity_total - quantity_produced - sum,
        };
    };

    onChangeCell = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const prev_value = this.state.split_orders_chunks[idx].quantity_open;
        const chunk_order = this.state.split_orders_chunks.find(c => c.idx === idx);
        if (chunk_order) {
            chunk_order.quantity_open = parseInt(e.currentTarget.value);
            // if diff is 1, we know user pressed arrow up or down
            if (Math.abs(chunk_order.quantity_open - prev_value) == 1) {
                if (chunk_order.quantity_open - prev_value > 0) {
                    // arrow up pressed, increase the quantity_open by norm_base_units
                    // (-1 is because it was added on arrow up press)
                    chunk_order.quantity_open += chunk_order.norm_base_units - 1;
                } else {
                    // arrow down pressed, decrease the quantity_open by norm_base_units
                    // (-1 is because it was added on arrow down press)
                    chunk_order.quantity_open -= chunk_order.norm_base_units - 1;
                }
            }
            // don't allow negative values (set min to quantity_produced value)
            if (chunk_order.quantity_open < chunk_order.quantity_produced) {
                chunk_order.quantity_open = chunk_order.quantity_produced;
            }
            chunk_order.quantity_total = chunk_order.quantity_open + chunk_order.quantity_produced;
            this.setState({ split_orders_chunks: [...this.state.split_orders_chunks] });
        }
    };

    onCellClick = (cell: Cell<any>) => {
        const is_remove = cell.column.id === "disable_remove";
        const uuid = cell.row.values.uuid;
        if (is_remove) {
            this.onClickRemove(uuid);
        }
    };

    getOrderUuid = (): string => {
        const chunk_order = LinesOrdersMaps.all_orders_map.get(this.props.order_uuid);
        if (chunk_order && chunk_order.parent_order_uuid) {
            return chunk_order.parent_order_uuid;
        }
        return this.props.order_uuid;
    };

    onClickOk = () => {
        const has_error = this.hasError();
        if (has_error) {
            return;
        }

        const chunked_orders = this.state.split_orders_chunks.map(c => ({
            uuid: c.uuid,
            parent_order_uuid: c.parent_order_uuid,
            quantity_total: c.quantity_total,
            line_uuid: c.line_uuid,
            quantity_produced: c.quantity_produced,
            external_id: c.external_id,
            process_num: c.process_num,
        }));

        const parent_order = this.getParentOrder();

        if (parent_order) {
            this.props.reduxDispatch({
                type: UPDATE_CHUNKED_ORDERS,
                data: {
                    parent_order_uuid: parent_order.uuid || "",
                    chunked_orders,
                },
            });
            if (chunked_orders.length === 0) {
                UserActions.onOrderClick({
                    order_uuid: parent_order.uuid,
                    order_external_id: parent_order.external_id,
                    source_type: "planning_table_parallel",
                    is_extra_line: false,
                    is_ctrl_down: false,
                    advanced_order_menu: null,
                    process_num: parent_order.process_num,
                    reduxDispatch: this.props.reduxDispatch,
                    is_readonly: false,
                    is_filter_locked: false,
                });
            }
        }
        this.props.onClose();
    };

    hasError = () => {
        if (this.state.split_orders_chunks.length === 0) {
            return false;
        }
        return (
            !this.checkSum().is_sum_ok ||
            this.state.split_orders_chunks.some(c => c.quantity_total < c.quantity_produced)
        );
    };

    getError = () => {
        const has_error = this.hasError();
        if (has_error) {
            const msg = (
                <p>
                    {translate("chunking_modal.error", "Some chunk orders have incorrect quantities.")}
                    {" " + translate("common.deviation", "Deviation") + ": " + this.checkSum().missing} <br />
                </p>
            );

            return <ErrorComponent msg={msg} type="warning" />;
        }
        return null;
    };

    getChunkData = () => {
        return this.state.split_orders_chunks.map(c => {
            const order = LinesOrdersMaps.all_orders_map.get(c.uuid);
            return {
                ...c,
                line_title: this.getLineTitle(c.line_uuid),
                // It is in our db
                disable_remove: Boolean(order && order.tags[ORDER_TAGS.split_chunk_index]),
            };
        });
    };

    render() {
        const has_error = this.hasError();
        const parent_order = this.getParentOrder();
        return (
            <DraggableModal>
                <Modal show={this.props.show}>
                    <Modal.Header>
                        <Modal.Title>{translate("common.chunk_orders", "Chunk orders")}</Modal.Title>
                        <button type="button" className="close" onClick={this.props.onClose}>
                            <span aria-hidden="true">×</span>
                            <span className="sr-only">{translate("common.close", "Close")}</span>
                        </button>
                    </Modal.Header>
                    <Modal.Body id="chunking-modal">
                        <ReactTable data={this.getParentOrderRow()} columns={getParentOrderColumns()} />
                        <ReactTable
                            data={this.getChunkData()}
                            columns={getChunkOrdersColumns()}
                            onChangeCell={this.onChangeCell}
                            onCellClick={this.onCellClick}
                            hiddenColumns={["uuid"]}
                        />
                        {this.getError()}
                        <button className="btn btn-primary" onClick={this.onClickPlus} style={{ width: "100%" }}>
                            +
                        </button>
                        <div className="text-secondary text-right ">
                            {translate("common.material_retort_units", "Number of units in one batch")}:{" "}
                            {parent_order != undefined ? parent_order.norm_base_units : "undefined"}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-primary" onClick={this.onClickOk} disabled={has_error}>
                            {translate("common.ok", "OK")}
                        </button>
                    </Modal.Footer>
                </Modal>
            </DraggableModal>
        );
    }
}

const mapState = (state: ReduxState) => {
    const { split_orders, lines_orders } = state.gantt_chart_lines_orders;
    return { split_orders, lines_orders };
};

const mapDispatch = (dispatch: Dispatch<OnOrderClickArgsActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(ChunkingModal);
